import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    FileText, Receipt, Plus, Loader2, Download, Mail, MailX, IndianRupee,
    Check, Ban, Undo2, AlertTriangle, Zap, ArrowRight, Settings, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import InvoicePreviewDialog from '@/components/superadmin/InvoicePreviewDialog';
import CompanySearchSelect from '@/components/superadmin/CompanySearchSelect';
import BillingContextPanel from '@/components/superadmin/BillingContextPanel';
import * as api from '@/services/apiClient';
import { toast } from 'sonner';

const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const ORDER_TONE = {
    draft:     'bg-muted text-muted-foreground border-border',
    confirmed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
    invoiced:  'bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/30',
    paid:      'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/30',
    activated: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
    cancelled: 'bg-destructive/10 text-destructive border-destructive/30',
    refunded:  'bg-destructive/10 text-destructive border-destructive/30',
};

const INVOICE_TONE = {
    issued:         'bg-muted text-muted-foreground border-border',
    sent:           'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
    partially_paid: 'bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/30',
    paid:           'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/30',
    overdue:        'bg-destructive/10 text-destructive border-destructive/30',
    cancelled:      'bg-muted text-muted-foreground border-border line-through',
    void:           'bg-muted text-muted-foreground border-border line-through',
};

function Pill({ tone, children }) {
    return (
        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}>
            {children}
        </span>
    );
}

/**
 * The order-to-cash trail.
 *
 * Every step is skippable, and a skipped step says so rather than leaving a
 * silent gap — "you did not do this" and "this did not need doing" look
 * identical otherwise, which is exactly the ambiguity that loses invoices.
 */
function OrderTrail({ order, invoice, blockers, companyName, companyId }) {
    const steps = useMemo(() => {
        const sellerMissing = blockers?.sellerMissing || [];
        const companyMissing = blockers?.companyMissing || [];

        return [
            {
                label: 'Draft order',
                done: true,
                detail: `${order.lineItems?.length || 0} line${order.lineItems?.length === 1 ? '' : 's'} · ${money(order.total)}`,
            },
            {
                label: 'Confirmed',
                done: !!order.orderNumber && order.status !== 'draft',
                detail: order.orderNumber ? `${order.orderNumber} · ${fmtDate(order.confirmedAt)}` : 'Locks the line items',
            },
            // Two separate steps, not one merged list. An invoice needs both
            // sides filled in, and showing a single "Missing: legal name,
            // address…" gave no clue whose details were meant — the answer was
            // usually the seller's, while the reader had just finished editing
            // the customer's.
            {
                label: 'Your billing settings',
                done: sellerMissing.length === 0,
                blocked: sellerMissing.length > 0,
                detail: sellerMissing.length === 0
                    ? 'Complete'
                    : `Missing: ${sellerMissing.join(', ')}`,
                link: sellerMissing.length > 0
                    ? { to: '/super-admin/billing/settings', label: 'Open Billing Settings' }
                    : null,
            },
            {
                label: `${companyName || 'Customer'}\u2019s billing details`,
                done: companyMissing.length === 0,
                blocked: companyMissing.length > 0,
                detail: companyMissing.length === 0
                    ? 'Complete'
                    : `Missing: ${companyMissing.join(', ')}`,
                link: companyMissing.length > 0 && companyId
                    ? { to: `/super-admin/companies/${companyId}`, label: 'Open company billing' }
                    : null,
            },
            {
                label: 'Billing email verified',
                done: !!blockers?.billingEmailVerified,
                blocked: !blockers?.billingEmailVerified,
                detail: blockers?.billingEmailVerified
                    ? 'Verified'
                    : 'Invoice email stays blocked until this is confirmed',
            },
            {
                label: 'Invoice issued',
                done: !!invoice,
                detail: invoice ? `${invoice.invoiceNumber} · due ${fmtDate(invoice.dueDate)}` : 'Assigns the invoice number',
            },
            {
                label: 'Emailed',
                done: !!invoice?.emailedAt,
                skipped: !!invoice?.emailSkipped && !invoice?.emailedAt,
                detail: invoice?.emailedAt
                    ? `Sent ${fmtDate(invoice.emailedAt)} to ${invoice.emailedTo?.join(', ')}`
                    : invoice?.emailSkipped ? 'Skipped deliberately' : 'Optional',
            },
            {
                label: 'Payment recorded',
                done: invoice?.status === 'paid',
                partial: invoice?.amountPaid > 0 && invoice?.status !== 'paid',
                detail: invoice
                    ? (invoice.amountPaid > 0
                        ? `${money(invoice.amountPaid)} of ${money(invoice.total)}`
                        : 'Nothing received yet')
                    : '—',
            },
            {
                label: 'Plan activated',
                done: order.status === 'activated',
                detail: order.status === 'activated'
                    ? (order.activatedWithoutPayment
                        ? `On credit terms — ${order.activationNote || 'no note'}`
                        : `Activated ${fmtDate(order.activatedAt)}`)
                    : 'Applies the new limits',
            },
        ];
    }, [order, invoice, blockers, companyName, companyId]);

    return (
        <ol className="space-y-0">
            {steps.map((s, i) => (
                <li key={s.label} className="relative flex gap-3 pb-4 last:pb-0">
                    {i < steps.length - 1 && (
                        <span className="absolute left-[11px] top-6 h-full w-px bg-border" aria-hidden="true" />
                    )}
                    <span className={`relative z-10 mt-0.5 flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-full border text-[10px] font-medium ${
                        s.done ? 'border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                            : s.skipped ? 'border-border bg-muted text-muted-foreground'
                            : s.partial ? 'border-amber-500 bg-amber-500/15 text-amber-600 dark:text-amber-500'
                            : s.blocked ? 'border-destructive/50 bg-destructive/10 text-destructive'
                            : 'border-border bg-card text-muted-foreground'
                    }`}>
                        {s.done ? <Check className="h-3 w-3" /> : s.skipped ? '–' : i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{s.label}</p>
                        <p className={`text-xs ${s.blocked ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {s.detail}
                        </p>
                        {s.link && (
                            <Link
                                to={s.link.to}
                                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            >
                                {s.link.label} <ArrowRight className="h-3 w-3" />
                            </Link>
                        )}
                    </div>
                </li>
            ))}
        </ol>
    );
}

function PaymentDialog({ invoice, open, onClose, onDone }) {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('bank_transfer');
    const [reference, setReference] = useState('');
    const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && invoice) setAmount(String(invoice.amountDue ?? ''));
    }, [open, invoice]);

    const submit = async () => {
        setSaving(true);
        try {
            const res = await api.recordInvoicePayment(invoice._id, {
                amount: Number(amount), method, reference,
                paidAt: new Date(paidAt).toISOString(),
            });
            toast.success(res.message);
            onDone();
        } catch (err) {
            toast.error(err.message || 'Could not record the payment.');
        } finally {
            setSaving(false);
        }
    };

    if (!invoice) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Record a payment</DialogTitle>
                    <DialogDescription>
                        {invoice.invoiceNumber} — {money(invoice.amountDue)} outstanding of {money(invoice.total)}.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <label className="text-sm font-medium">Amount received</label>
                        <Input type="number" step="0.01" min="0" value={amount}
                            onChange={e => setAmount(e.target.value)} className="mt-1.5" />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            Part payments are fine — the balance stays open and reminders continue.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium">Method</label>
                            <select value={method} onChange={e => setMethod(e.target.value)}
                                className="mt-1.5 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                                <option value="bank_transfer">Bank transfer</option>
                                <option value="upi">UPI</option>
                                <option value="cheque">Cheque</option>
                                <option value="card">Card</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Date received</label>
                            <Input type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} className="mt-1.5" />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium">Reference</label>
                        <Input value={reference} onChange={e => setReference(e.target.value)}
                            placeholder="UTR / cheque number" className="mt-1.5" />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !(Number(amount) > 0)} className="gap-2">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />}
                        Record payment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Creates a draft order.
 *
 * Draft only — nothing is numbered, invoiced or charged from here. Confirming
 * is a separate, deliberate step, so a half-finished order costs nothing.
 */
function NewOrderDialog({ open, onClose, onCreated }) {
    const [companies, setCompanies] = useState([]);
    const [plans, setPlans] = useState([]);
    const [companyId, setCompanyId] = useState('');
    const [planId, setPlanId] = useState('');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [periodStart, setPeriodStart] = useState(() => new Date().toISOString().slice(0, 10));
    const [discount, setDiscount] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [context, setContext] = useState(null);
    const [contextLoading, setContextLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setContext(null);
        Promise.all([api.getAllCompanies(), api.getActivePlans()])
            .then(([c, p]) => {
                setCompanies(c.data || []);
                setPlans(p.data || []);
            })
            .catch(() => toast.error('Could not load companies and plans.'));
    }, [open]);

    // Pull the company's billing position as soon as one is chosen, and take
    // the suggested start unless the date has already been touched by hand.
    useEffect(() => {
        if (!companyId) { setContext(null); return; }

        let cancelled = false;
        setContextLoading(true);
        api.getOrderContext(companyId)
            .then(res => {
                if (cancelled) return;
                setContext(res.data);
                if (res.data.suggestedPeriodStart) {
                    setPeriodStart(new Date(res.data.suggestedPeriodStart).toISOString().slice(0, 10));
                }
            })
            .catch(() => { if (!cancelled) setContext(null); })
            .finally(() => { if (!cancelled) setContextLoading(false); });

        return () => { cancelled = true; };
    }, [companyId]);

    const selectedPlan = plans.find(p => p._id === planId);

    // Days billed twice if this start predates the cover already paid for.
    const coveredUntil = context?.subscription?.coveredUntil;
    const overlapDays = coveredUntil && periodStart
        ? Math.max(0, Math.ceil((new Date(coveredUntil) - new Date(periodStart)) / 86400000))
        : 0;
    const months = { monthly: 1, quarterly: 3, annual: 12 }[billingCycle] || 1;
    const gross = selectedPlan ? selectedPlan.price * months : 0;
    const net = Math.max(0, gross - (Number(discount) || 0));

    const submit = async () => {
        setSaving(true);
        try {
            const lineItems = [];
            if (selectedPlan) {
                lineItems.push({
                    type: 'plan',
                    description: `${selectedPlan.name} — ${billingCycle}${months > 1 ? ` (${months} months)` : ''}`,
                    quantity: months,
                    unitPrice: selectedPlan.price,
                    amount: gross,
                });
            }
            if (Number(discount) > 0) {
                lineItems.push({
                    type: 'discount',
                    description: 'Discount',
                    quantity: 1,
                    unitPrice: Number(discount),
                    amount: Number(discount),
                });
            }

            const res = await api.createOrder({
                companyId, planId: planId || null, billingCycle,
                periodStart: new Date(periodStart).toISOString(),
                lineItems, notes,
            });
            toast.success(res.message || 'Draft order created.');
            onCreated(res.data?._id);
        } catch (err) {
            toast.error(err.message || 'Could not create the order.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>New order</DialogTitle>
                    <DialogDescription>
                        Creates a draft. Nothing is numbered or charged until you confirm it.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <label className="text-sm font-medium">Company</label>
                        <div className="mt-1.5">
                            <CompanySearchSelect
                                companies={companies}
                                value={companyId}
                                onChange={setCompanyId}
                            />
                        </div>
                    </div>

                    {(context || contextLoading) && (
                        <BillingContextPanel
                            context={context}
                            loading={contextLoading}
                            onUseSuggested={(d) => setPeriodStart(new Date(d).toISOString().slice(0, 10))}
                        />
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium">Plan</label>
                            <select value={planId} onChange={e => setPlanId(e.target.value)}
                                className="mt-1.5 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                                <option value="">No plan</option>
                                {plans.map(p => (
                                    <option key={p._id} value={p._id}>
                                        {p.name} — {money(p.price)}/mo
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Billing cycle</label>
                            <select value={billingCycle} onChange={e => setBillingCycle(e.target.value)}
                                className="mt-1.5 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly (×3)</option>
                                <option value="annual">Annual (×12)</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium">Period starts</label>
                            <Input type="date" value={periodStart}
                                onChange={e => setPeriodStart(e.target.value)} className="mt-1.5" />
                            {overlapDays > 0 && (
                                <p className="mt-1 text-[11px] font-medium text-amber-600 dark:text-amber-500">
                                    Overlaps {overlapDays} day{overlapDays === 1 ? '' : 's'} already covered.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium">Discount</label>
                            <Input type="number" min="0" step="0.01" value={discount}
                                onChange={e => setDiscount(e.target.value)} placeholder="0" className="mt-1.5" />
                            <p className="mt-1 text-[11px] text-muted-foreground">Applied before tax.</p>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium">Notes</label>
                        <Input value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="Shown on the invoice" className="mt-1.5" />
                    </div>

                    {selectedPlan && (
                        <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">{months} month{months === 1 ? '' : 's'} of {selectedPlan.name}</span>
                                <span className="tabular-nums">{money(gross)}</span>
                            </div>
                            {Number(discount) > 0 && (
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Discount</span>
                                    <span className="tabular-nums">-{money(Number(discount))}</span>
                                </div>
                            )}
                            <div className="mt-1.5 flex justify-between border-t border-border pt-1.5 font-medium">
                                <span>Before tax</span>
                                <span className="tabular-nums">{money(net)}</span>
                            </div>
                            <p className="mt-1.5 text-[11px] text-muted-foreground">
                                GST is added on the order once the company&apos;s state is known.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !companyId} className="gap-2">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Create draft
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function SuperAdminBillingPage() {
    const [orders, setOrders] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);      // { order, invoice, blockers }
    const [busy, setBusy] = useState(false);
    const [payTarget, setPayTarget] = useState(null);
    const [tab, setTab] = useState('orders');
    const [newOrderOpen, setNewOrderOpen] = useState(false);
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [orderContext, setOrderContext] = useState(null);

    const load = useCallback(async () => {
        try {
            const [o, i] = await Promise.all([api.listOrders(), api.listInvoices()]);
            setOrders(o.data || []);
            setInvoices(i.data || []);
        } catch (err) {
            toast.error(err.message || 'Could not load billing data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openOrder = async (id) => {
        try {
            const res = await api.getOrder(id);
            setSelected(res.data);
            // Whether this order's period still makes sense depends on what has
            // happened to the company since it was drafted.
            setOrderContext(null);
            api.getOrderContext(res.data.order.companyId)
                .then(c => setOrderContext(c.data))
                .catch(() => setOrderContext(null));
        } catch (err) {
            toast.error(err.message || 'Could not open this order.');
        }
    };

    const act = async (fn, successMsg) => {
        setBusy(true);
        try {
            const res = await fn();
            toast.success(successMsg || res?.message || 'Done.');
            await load();
            if (selected?.order?._id) await openOrder(selected.order._id);
        } catch (err) {
            toast.error(err.message || 'That did not work.');
        } finally {
            setBusy(false);
        }
    };

    const totals = useMemo(() => {
        const open = invoices.filter(i => ['issued', 'sent', 'partially_paid', 'overdue'].includes(i.status));
        const overdue = invoices.filter(i => i.status === 'overdue');
        return {
            outstanding: open.reduce((s, i) => s + (i.amountDue || 0), 0),
            overdueAmount: overdue.reduce((s, i) => s + (i.amountDue || 0), 0),
            overdueCount: overdue.length,
            draftCount: orders.filter(o => o.status === 'draft').length,
        };
    }, [invoices, orders]);

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-10">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Billing</h1>
                    <p className="text-sm text-muted-foreground">Orders, invoices and payments.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" asChild className="gap-2">
                        <Link to="/super-admin/billing/settings">
                            <Settings className="h-4 w-4" /> Billing settings
                        </Link>
                    </Button>
                    <Button onClick={() => setNewOrderOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" /> New order
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <DashboardKpiCard title="Outstanding" value={money(totals.outstanding)}
                    subtitle="Invoiced, not yet paid" icon={Receipt} tone="primary" />
                <DashboardKpiCard title="Overdue" value={money(totals.overdueAmount)}
                    subtitle={`${totals.overdueCount} invoice${totals.overdueCount === 1 ? '' : 's'} past due`}
                    icon={AlertTriangle} tone={totals.overdueCount > 0 ? 'destructive' : 'default'} />
                <DashboardKpiCard title="Draft orders" value={totals.draftCount}
                    subtitle="Not yet confirmed" icon={FileText} tone="default" />
                <DashboardKpiCard title="Invoices" value={invoices.length}
                    subtitle="Issued all time" icon={Receipt} tone="default" />
            </div>

            <div className="flex gap-1 border-b border-border">
                {[['orders', 'Orders', orders.length], ['invoices', 'Invoices', invoices.length]].map(([key, label, count]) => (
                    <button key={key} type="button" onClick={() => setTab(key)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                            tab === key
                                ? 'border-primary text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}>
                        {label} <span className="text-xs text-muted-foreground">({count})</span>
                    </button>
                ))}
            </div>

            {tab === 'orders' && (
                <div className="overflow-x-auto rounded-xl border border-border bg-card">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left">
                                <th className="p-3 font-medium text-muted-foreground">Order</th>
                                <th className="p-3 font-medium text-muted-foreground">Company</th>
                                <th className="p-3 font-medium text-muted-foreground">Plan</th>
                                <th className="p-3 text-right font-medium text-muted-foreground">Total</th>
                                <th className="p-3 font-medium text-muted-foreground">Status</th>
                                <th className="p-3 font-medium text-muted-foreground">Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {orders.length === 0 && (
                                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                                    No orders yet.
                                </td></tr>
                            )}
                            {orders.map(o => (
                                <tr key={o._id} className="cursor-pointer hover:bg-muted/40" onClick={() => openOrder(o._id)}>
                                    <td className="p-3 font-mono text-xs">{o.orderNumber || 'Draft'}</td>
                                    <td className="p-3 font-medium">{o.companyId}</td>
                                    <td className="p-3 text-muted-foreground">{o.planName || 'No plan'}</td>
                                    <td className="p-3 text-right font-medium tabular-nums">{money(o.total)}</td>
                                    <td className="p-3"><Pill tone={ORDER_TONE[o.status]}>{o.status}</Pill></td>
                                    <td className="p-3 text-muted-foreground">{fmtDate(o.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'invoices' && (
                <div className="overflow-x-auto rounded-xl border border-border bg-card">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left">
                                <th className="p-3 font-medium text-muted-foreground">Invoice</th>
                                <th className="p-3 font-medium text-muted-foreground">Company</th>
                                <th className="p-3 text-right font-medium text-muted-foreground">Total</th>
                                <th className="p-3 text-right font-medium text-muted-foreground">Due</th>
                                <th className="p-3 font-medium text-muted-foreground">Status</th>
                                <th className="p-3 font-medium text-muted-foreground">Due date</th>
                                <th className="p-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {invoices.length === 0 && (
                                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                                    No invoices issued yet.
                                </td></tr>
                            )}
                            {invoices.map(inv => (
                                <tr key={inv._id} className="hover:bg-muted/40">
                                    <td className="p-3 font-mono text-xs">{inv.invoiceNumber}</td>
                                    <td className="p-3 font-medium">{inv.companyId}</td>
                                    <td className="p-3 text-right tabular-nums">{money(inv.total)}</td>
                                    <td className="p-3 text-right font-medium tabular-nums">{money(inv.amountDue)}</td>
                                    <td className="p-3"><Pill tone={INVOICE_TONE[inv.status]}>{inv.status.replace('_', ' ')}</Pill></td>
                                    <td className="p-3 text-muted-foreground">{fmtDate(inv.dueDate)}</td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
                                                onClick={() => setPreviewInvoice(inv)}>
                                                <Eye className="h-3 w-3" /> View
                                            </Button>
                                            {inv.amountDue > 0 && (
                                                <Button size="sm" className="h-7 gap-1 text-xs" onClick={() => setPayTarget(inv)}>
                                                    <IndianRupee className="h-3 w-3" /> Payment
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Order detail */}
            <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setOrderContext(null); }}>
                <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
                    {selected && (() => {
                    // An invoice needs both sides complete. Offering the button
                    // anyway just moves the failure to a toast after the click.
                    const invoiceBlocked =
                        (selected.blockers?.sellerMissing?.length || 0) > 0 ||
                        (selected.blockers?.companyMissing?.length || 0) > 0;
                    return (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    {selected.order.orderNumber || 'Draft order'}
                                    <Pill tone={ORDER_TONE[selected.order.status]}>{selected.order.status}</Pill>
                                </DialogTitle>
                                <DialogDescription>
                                    {selected.company?.companyName || selected.order.companyId} ·{' '}
                                    {selected.order.planName || 'No plan'} · {selected.order.billingCycle}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid gap-6 py-2 sm:grid-cols-2">
                                <div>
                                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        Progress
                                    </h3>
                                    <OrderTrail
                                        order={selected.order}
                                        invoice={selected.invoice}
                                        blockers={selected.blockers}
                                        companyName={selected.company?.companyName}
                                        companyId={selected.order.companyId}
                                    />
                                </div>

                                <div>
                                    {orderContext && (
                                        <div className="mb-4">
                                            <BillingContextPanel context={orderContext} title="Company position now" />
                                        </div>
                                    )}
                                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        Amounts
                                    </h3>
                                    <dl className="space-y-1.5 text-sm">
                                        {selected.order.lineItems?.map((li, idx) => (
                                            <div key={idx} className="flex justify-between gap-3">
                                                <dt className="text-muted-foreground">{li.description}</dt>
                                                <dd className="tabular-nums">
                                                    {li.type === 'discount' ? `-${money(li.amount)}` : money(li.amount)}
                                                </dd>
                                            </div>
                                        ))}
                                        <div className="flex justify-between border-t border-border pt-1.5">
                                            <dt className="text-muted-foreground">Taxable</dt>
                                            <dd className="tabular-nums">{money(selected.order.taxableValue)}</dd>
                                        </div>
                                        {selected.order.tax?.mode === 'cgst_sgst' && (
                                            <>
                                                <div className="flex justify-between">
                                                    <dt className="text-muted-foreground">CGST @ {selected.order.tax.rate / 2}%</dt>
                                                    <dd className="tabular-nums">{money(selected.order.tax.cgst)}</dd>
                                                </div>
                                                <div className="flex justify-between">
                                                    <dt className="text-muted-foreground">SGST @ {selected.order.tax.rate / 2}%</dt>
                                                    <dd className="tabular-nums">{money(selected.order.tax.sgst)}</dd>
                                                </div>
                                            </>
                                        )}
                                        {selected.order.tax?.mode === 'igst' && (
                                            <div className="flex justify-between">
                                                <dt className="text-muted-foreground">IGST @ {selected.order.tax.rate}%</dt>
                                                <dd className="tabular-nums">{money(selected.order.tax.igst)}</dd>
                                            </div>
                                        )}
                                        <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
                                            <dt>Total</dt>
                                            <dd className="tabular-nums">{money(selected.order.total)}</dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>

                            <DialogFooter className="flex-wrap gap-2 border-t border-border pt-4">
                                {selected.order.status === 'draft' && (
                                    <Button size="sm" disabled={busy} className="gap-1.5"
                                        onClick={() => act(() => api.confirmOrder(selected.order._id))}>
                                        <Check className="h-3.5 w-3.5" /> Confirm order
                                    </Button>
                                )}
                                {selected.order.status === 'confirmed' && (
                                    <>
                                        <Button size="sm" variant="outline" disabled={busy} className="gap-1.5"
                                            onClick={() => act(() => api.reopenOrder(selected.order._id))}>
                                            <Undo2 className="h-3.5 w-3.5" /> Reopen as draft
                                        </Button>
                                        <Button
                                            size="sm"
                                            disabled={busy || invoiceBlocked}
                                            className="gap-1.5"
                                            title={invoiceBlocked
                                                ? 'Billing details are incomplete — see the steps on the left.'
                                                : undefined}
                                            onClick={() => act(() => api.issueInvoiceForOrder(selected.order._id))}>
                                            <Receipt className="h-3.5 w-3.5" /> Issue invoice
                                        </Button>
                                    </>
                                )}
                                {selected.invoice && (
                                    <>
                                        <Button size="sm" variant="outline" className="gap-1.5"
                                            onClick={() => api.downloadInvoicePdf(selected.invoice._id, selected.invoice.invoiceNumber)
                                                .catch(e => toast.error(e.message))}>
                                            <Download className="h-3.5 w-3.5" /> PDF
                                        </Button>
                                        {!selected.invoice.emailedAt && (
                                            <>
                                                <Button size="sm" variant="outline" disabled={busy} className="gap-1.5"
                                                    onClick={() => setPreviewInvoice(selected.invoice)}>
                                                    <Eye className="h-3.5 w-3.5" /> Review &amp; send
                                                </Button>
                                                {!selected.invoice.emailSkipped && (
                                                    <Button size="sm" variant="ghost" disabled={busy} className="gap-1.5"
                                                        onClick={() => act(() => api.skipInvoiceEmail(selected.invoice._id))}>
                                                        <MailX className="h-3.5 w-3.5" /> Skip email
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        {selected.invoice.amountDue > 0 && (
                                            <Button size="sm" disabled={busy} className="gap-1.5"
                                                onClick={() => setPayTarget(selected.invoice)}>
                                                <IndianRupee className="h-3.5 w-3.5" /> Record payment
                                            </Button>
                                        )}
                                    </>
                                )}
                                {['confirmed', 'invoiced'].includes(selected.order.status) && (
                                    <Button size="sm" variant="outline" disabled={busy} className="gap-1.5"
                                        onClick={() => {
                                            const note = window.prompt('Why is this being activated before payment?');
                                            if (note?.trim()) act(() => api.activateOrderWithoutPayment(selected.order._id, note.trim()));
                                        }}>
                                        <Zap className="h-3.5 w-3.5" /> Activate on credit
                                    </Button>
                                )}
                                {selected.order.status !== 'activated' && selected.order.status !== 'cancelled' && (
                                    <Button size="sm" variant="ghost" disabled={busy}
                                        className="gap-1.5 text-destructive hover:text-destructive"
                                        onClick={() => {
                                            const reason = window.prompt('Reason for cancelling this order?');
                                            if (reason?.trim()) act(() => api.cancelOrder(selected.order._id, reason.trim()));
                                        }}>
                                        <Ban className="h-3.5 w-3.5" /> Cancel
                                    </Button>
                                )}
                            </DialogFooter>
                        </>
                    );
                    })()}
                </DialogContent>
            </Dialog>

            <InvoicePreviewDialog
                invoice={previewInvoice}
                open={!!previewInvoice}
                onClose={() => setPreviewInvoice(null)}
                onSent={() => { setPreviewInvoice(null); load(); if (selected?.order?._id) openOrder(selected.order._id); }}
                onUpdated={(updated) => { setPreviewInvoice(updated); load(); if (selected?.order?._id) openOrder(selected.order._id); }}
            />

            <NewOrderDialog
                open={newOrderOpen}
                onClose={() => setNewOrderOpen(false)}
                onCreated={(id) => { setNewOrderOpen(false); load(); if (id) openOrder(id); }}
            />

            <PaymentDialog
                invoice={payTarget}
                open={!!payTarget}
                onClose={() => setPayTarget(null)}
                onDone={() => { setPayTarget(null); load(); if (selected?.order?._id) openOrder(selected.order._id); }}
            />
        </div>
    );
}
